import { CanonicalizeError } from '@/errors'
import { canonicalizeNumber } from '@/number'
import { canonicalizeString } from '@/string'
import type { Options } from '@/types'

export { CanonicalizeError } from '@/errors'
export type { CanonicalizeErrorCode } from '@/errors'
export type { JsonValue, Options } from '@/types'

/**
 * Canonicalize a JavaScript value into a single deterministic string form
 * that complies with RFC 8785 (JCS).
 *
 * Semantics match JSON.stringify where the spec is silent:
 *   1. If a value exposes a toJSON method, that method is called before
 *      type dispatch.
 *   2. An optional replacer callback runs on every key and value and can
 *      transform the value or omit it.
 *   3. undefined inside an object causes the key to be dropped, undefined
 *      inside an array emits null, and undefined at the root throws.
 *   4. A circular reference throws CIRCULAR_REFERENCE. The cycle detector
 *      uses a per call ancestor set with delete on ascent, so a shared
 *      descendant reached from sibling positions is not flagged.
 */
export function canonicalize(value: unknown, options: Options = {}): string {
  const rootValue = transformRoot(value, options)
  if (rootValue === undefined) {
    throw new CanonicalizeError(
      'UNSUPPORTED_TYPE',
      'Root value resolved to undefined and canonical output must be a string',
      []
    )
  }
  return encode(rootValue, [], options, new Set())
}

/**
 * Canonicalize a value and return the UTF-8 byte sequence of the canonical
 * string. Most callers want bytes because the next step is usually a hash or
 * signature, both of which operate on bytes. Uses the platform TextEncoder,
 * which is available in every modern JavaScript runtime (Node, Bun, Deno,
 * browsers, and Workers).
 */
export function canonicalizeBytes(value: unknown, options: Options = {}): Uint8Array {
  return new TextEncoder().encode(canonicalize(value, options))
}

function transformRoot(value: unknown, options: Options): unknown {
  const afterToJSON = applyToJSON(value, '')
  if (!options.replacer) {
    return afterToJSON
  }
  const rootWrapper = { '': afterToJSON }
  return options.replacer.call(rootWrapper, '', afterToJSON)
}

function applyToJSON(value: unknown, key: string): unknown {
  if (
    value !== null &&
    typeof value === 'object' &&
    'toJSON' in value &&
    typeof (value as { toJSON?: unknown }).toJSON === 'function'
  ) {
    return (value as { toJSON: (keyArgument: string) => unknown }).toJSON(key)
  }
  return value
}

function encode(value: unknown, path: readonly string[], options: Options, ancestors: Set<object>): string {
  if (value === null) {
    return 'null'
  }
  const valueType = typeof value
  if (valueType === 'boolean') {
    return value ? 'true' : 'false'
  }
  if (valueType === 'number') {
    return canonicalizeNumber(value as number, path)
  }
  if (valueType === 'string') {
    return canonicalizeString(value as string)
  }
  if (Array.isArray(value)) {
    return encodeArray(value, path, options, ancestors)
  }
  if (valueType === 'object') {
    return encodeObject(value as Record<string, unknown>, path, options, ancestors)
  }
  throw new CanonicalizeError('UNSUPPORTED_TYPE', `Unsupported type: ${valueType}`, path)
}

function encodeArray(arrayValue: unknown[], path: readonly string[], options: Options, ancestors: Set<object>): string {
  if (ancestors.has(arrayValue)) {
    throw new CanonicalizeError(
      'CIRCULAR_REFERENCE',
      'Array references one of its ancestors and cannot be canonicalized',
      path
    )
  }
  ancestors.add(arrayValue)
  try {
    const parts: string[] = []
    for (let index = 0; index < arrayValue.length; index++) {
      const indexKey = String(index)
      const childPath = [...path, indexKey]
      let currentValue: unknown = applyToJSON(arrayValue[index], indexKey)
      if (options.replacer) {
        currentValue = options.replacer.call(arrayValue, indexKey, currentValue)
      }
      // RFC 8785 does not cover undefined, so we follow JSON.stringify and
      // emit the literal null keyword in array positions that resolve to
      // undefined.
      if (currentValue === undefined) {
        parts.push('null')
      } else {
        parts.push(encode(currentValue, childPath, options, ancestors))
      }
    }
    return '[' + parts.join(',') + ']'
  } finally {
    ancestors.delete(arrayValue)
  }
}

function encodeObject(
  objectValue: Record<string, unknown>,
  path: readonly string[],
  options: Options,
  ancestors: Set<object>
): string {
  if (ancestors.has(objectValue)) {
    throw new CanonicalizeError(
      'CIRCULAR_REFERENCE',
      'Object references one of its ancestors and cannot be canonicalized',
      path
    )
  }
  ancestors.add(objectValue)
  try {
    const sortedKeys = Object.keys(objectValue).sort()
    const parts: string[] = []
    for (const key of sortedKeys) {
      const childPath = [...path, key]
      let currentValue: unknown = applyToJSON(objectValue[key], key)
      if (options.replacer) {
        currentValue = options.replacer.call(objectValue, key, currentValue)
      }
      // An object value of undefined causes the key to be dropped entirely,
      // so the output matches what JSON.stringify would produce.
      if (currentValue === undefined) {
        continue
      }
      parts.push(canonicalizeString(key) + ':' + encode(currentValue, childPath, options, ancestors))
    }
    return '{' + parts.join(',') + '}'
  } finally {
    ancestors.delete(objectValue)
  }
}
